import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '@/utils/authUtils';
import { useAuth } from './AuthContext';
import { CelebrationPopup } from '@/components/CelebrationPopup';
import XPPopup from '@/components/XPPopup';
import { useLanguage } from './LanguageContext';
import { apiFetch } from '@/hooks/useFetch';
import { devLog } from '@/utils/devLog';

const API_URL = getApiUrl();

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const { user, isAuthenticated, updateUser } = useAuth();
  const { isSpanish } = useLanguage();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Celebration popup state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationType, setCelebrationType] = useState('goal');
  const [celebrationValue, setCelebrationValue] = useState(1);
  
  // XP popup state
  const [showXPPopup, setShowXPPopup] = useState(false);
  const [xpData, setXpData] = useState(null);

  const DAILY_FREE_LIMIT = 999; // No limits — VIP is ad-free only

  // Fetch subscription status
  const fetchStatus = useCallback(async () => {
    try {
      const { data, error } = await apiFetch('/api/subscription/status');
      if (!error && data) {
        setSubscriptionStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, isAuthenticated]);

  // Auto-detect and set user timezone on login
  useEffect(() => {
    const setTimezone = async () => {
      if (!isAuthenticated) return;
      
      try {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (userTimezone) {
          await apiFetch('/api/subscription/set-timezone', {
            method: 'POST',
            body: { timezone: userTimezone }
          });
        }
      } catch (error) {
        console.error('Failed to set timezone:', error);
      }
    };
    
    setTimezone();
  }, [isAuthenticated]);

  // Track phrase view with celebration trigger
  const trackPhraseView = async (phraseId) => {
    if (!isAuthenticated) return false;
    
    try {
      const { data, error } = await apiFetch('/api/subscription/track-view', {
        method: 'POST',
        body: { phrase_id: phraseId }
      });
      
      if (!error && data?.tracked) {
        setSubscriptionStatus(prev => prev ? {
          ...prev,
          daily_views: data.daily_views,
          views_reset_at: data.views_reset_at
        } : null);

        // Let goal/streak UI (QuickStats, StreakGoalCard) know a view happened
        // so they can re-fetch the authoritative progress from the backend.
        try {
          window.dispatchEvent(new CustomEvent('phrase-viewed', {
            detail: { phraseId, goal_progress: data.goal_progress }
          }));
        } catch { /* ignore in non-browser envs */ }
        
        // Check for celebration triggers
        if (data.streak?.celebration) {
          // First phrase of the day - celebrate streak!
          setCelebrationType('streak');
          setCelebrationValue(data.streak.current);
          setShowCelebration(true);
        } else if (data.goal_progress?.celebration) {
          // Daily goal just completed!
          setCelebrationType('goal');
          setCelebrationValue(data.goal_progress.target);
          setShowCelebration(true);
        }
        
        // Update user data with new streak info
        if (updateUser && data.streak) {
          updateUser({
            current_streak: data.streak.current,
            longest_streak: data.streak.longest,
            total_xp: data.xp?.total,
            level: data.xp?.level
          });
        }
        
        // Show XP popup if XP was earned
        if (data.xp && data.xp.earned > 0) {
          setXpData(data.xp);
          setShowXPPopup(true);
        }

        // Push GTM dataLayer events for milestones
        try {
          const { track } = await import('@/lib/analytics');
          if (data.xp?.level_up && data.xp?.level) {
            track.levelUp(data.xp.level);
          }
          if (data.streak?.celebration && data.streak?.current
              && [3, 7, 14, 30, 60, 100, 365].includes(data.streak.current)) {
            track.streakMilestone(data.streak.current);
          }
        } catch { /* analytics never blocks UX */ }
        
        return data.tracked;
      }
    } catch (error) {
      console.error('Failed to track phrase view:', error);
    }
    return false;
  };

  // Start free trial
  const startTrial = async () => {
    try {
      const { data, error } = await apiFetch('/api/subscription/start-trial', {
        method: 'POST'
      });
      
      if (error) {
        throw new Error(error || 'Failed to start trial');
      }
      
      setSubscriptionStatus(prev => ({
        ...prev,
        status: 'trial',
        trial_ends_at: data.trial_ends_at,
        can_view_phrases: true
      }));
      
      if (updateUser) {
        updateUser({ subscription_status: 'trial', trial_ends_at: data.trial_ends_at });
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  // Create checkout session
  const createCheckout = async () => {
    try {
      devLog('Creating checkout session...');
      const { data, error } = await apiFetch('/api/subscription/create-checkout', {
        method: 'POST',
        body: { origin_url: window.location.origin }
      });
      
      if (error) {
        console.error('Checkout error:', error);
        throw new Error(error || 'Failed to create checkout');
      }
      
      devLog('Checkout session created, redirecting to:', data.url);
      
      // Redirect to Stripe checkout in the SAME window (not a new tab)
      if (data.url) {
        // Use replace to redirect in the same window without creating browser history
        window.location.replace(data.url);
      } else {
        throw new Error('No checkout URL received');
      }
      return data;
    } catch (error) {
      console.error('Checkout failed:', error);
      throw error;
    }
  };

  // Check checkout status (for success page)
  const checkCheckoutStatus = async (sessionId) => {
    try {
      const { data, error } = await apiFetch(`/api/subscription/checkout-status/${sessionId}`);
      
      if (error) {
        throw new Error('Failed to check checkout status');
      }
      
      if (data?.payment_status === 'paid') {
        await fetchStatus(); // Refresh subscription status
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  // Cancel subscription
  const cancelSubscription = async () => {
    try {
      const { data, error } = await apiFetch('/api/subscription/cancel', {
        method: 'POST'
      });
      
      if (error) {
        throw new Error(error || 'Failed to cancel subscription');
      }
      
      await fetchStatus();
      return data;
    } catch (error) {
      throw error;
    }
  };

  // Check if user can view more phrases
  const canViewPhrase = () => {
    // Admin users have unlimited access
    if (user?.is_admin) return true;
    if (!subscriptionStatus) return true; // Allow while loading
    return subscriptionStatus.can_view_phrases;
  };

  // Check if user needs to upgrade
  const needsUpgrade = () => {
    // Admin users never need to upgrade
    if (user?.is_admin) return false;
    if (!subscriptionStatus) return false;
    return !subscriptionStatus.can_view_phrases;
  };

  // Get remaining free views for today
  const getRemainingFreeViews = () => {
    if (!subscriptionStatus) return DAILY_FREE_LIMIT;
    const dailyViews = subscriptionStatus.daily_views || 0;
    return Math.max(0, DAILY_FREE_LIMIT - dailyViews);
  };

  // Check if user is a free user (not subscribed)
  const isFreeUser = () => {
    if (!subscriptionStatus) return true;
    return subscriptionStatus.status === 'free' || subscriptionStatus.status === 'expired';
  };

  const value = {
    subscriptionStatus,
    isLoading,
    trackPhraseView,
    startTrial,
    createCheckout,
    checkCheckoutStatus,
    cancelSubscription,
    canViewPhrase,
    needsUpgrade,
    getRemainingFreeViews,
    isFreeUser,
    fetchStatus,
    FREE_PHRASE_LIMIT: DAILY_FREE_LIMIT,
    DAILY_FREE_LIMIT
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      <CelebrationPopup
        isOpen={showCelebration}
        onClose={() => setShowCelebration(false)}
        type={celebrationType}
        value={celebrationValue}
        isSpanish={isSpanish}
      />
      <XPPopup
        xpData={xpData}
        isVisible={showXPPopup}
        onClose={() => setShowXPPopup(false)}
      />
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
